import { ElementType } from 'react';
import { SystemMessageBlock, SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFundIdFromPath from '@/hooks/useFundIdFromPath';
import { resolveAnnouncementUrl } from '@/components/Announcements/openAnnouncement';
import { AnnouncementImage } from '@/components/Announcements/AnnouncementImage';
import { familyClassForType, iconForMessage, systemMessageIcons } from './systemMessageIcons';
import styles from './SystemMessageCard.module.scss';

/** Everything the card needs. A draft in the editor is not saved yet, so it has no id or dates. */
export type SystemMessageCardData = Pick<SystemMessageDtoIn, 'eyebrow' | 'title' | 'body' | 'type' | 'iconKey' | 'blocks'>;

interface SystemMessageCardProps {
  message: SystemMessageCardData;
  /** Rendered by the admin's live preview, where nothing should be clickable. */
  isPreview?: boolean;
  /**
   * What to render the headline as. The popup passes Radix's Dialog.Title, so the message's own
   * heading is what names the dialog - rather than a second, hidden copy of the same words that a
   * screen reader would then read twice.
   */
  TitleTag?: ElementType;
}

/**
 * One system message, laid out. Shared by the popup users see and the preview an admin edits
 * against, so what the editor shows is the thing itself rather than an approximation of it.
 */
const SystemMessageCard = ({ message, isPreview = false, TitleTag = 'h2' }: SystemMessageCardProps) => {
  /*
   * The fund a `{fund}` in a button's address stands for (HAT-1489): the one on screen, else the
   * reader's first. The card is drawn in Layout and in the management area, both outside FundProvider.
   */
  const { currentUser } = useAuthContext();
  const fundIdFromPath = useFundIdFromPath();
  const fundId = fundIdFromPath ?? currentUser?.fundCodes?.[0] ?? currentUser?.fundIds?.[0] ?? null;
  const family = familyClassForType[message.type] ?? 'blue';

  return (
    <div className={`${styles.card} ${styles[family]}`}>
      <div className={styles.header}>
        <span className={styles.iconBadge}>
          <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            {iconForMessage(message)}
          </svg>
        </span>
        <div className={styles.headings}>
          {message.eyebrow && <p className={`${styles.eyebrow} body-sm-bold`}>{message.eyebrow}</p>}
          <TitleTag className={`${styles.title} heading-h3-bold`}>{message.title}</TitleTag>
        </div>
      </div>

      {(message.body || message.blocks.length > 0) && (
        <div className={styles.blocks}>
          {message.body && <p className={`${styles.paragraph} body-md-regular`}>{message.body}</p>}
          {message.blocks.map((block, index) => (
            <Block key={index} block={block} isPreview={isPreview} fundId={fundId} />
          ))}
        </div>
      )}
    </div>
  );
};

const Block = ({ block, isPreview, fundId }: { block: SystemMessageBlock; isPreview: boolean; fundId: string | number | null }) => {
  switch (block.kind) {
    case 'heading':
      return <h3 className={`${styles.heading} body-lr-bold`}>{block.text}</h3>;

    case 'bullets':
      return (
        <ul className={styles.bullets}>
          {(block.items ?? []).map((item, index) => (
            <li key={index} className={`${styles.bullet} body-md-regular`}>
              <span className={styles.bulletDot} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'callout':
      return (
        <div className={styles.callout}>
          <svg className={styles.calloutIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            {systemMessageIcons.info}
          </svg>
          <p className={`${styles.calloutText} body-sm-regular`}>{block.text}</p>
        </div>
      );

    case 'buttons':
      return (
        <div className={styles.buttons}>
          {(block.buttons ?? []).map((button, index) => {
            const className = `${styles.linkButton} ${button.variant === 'primary' ? styles.linkPrimary : styles.linkSecondary} body-sm-semibold`;

            // The preview must not navigate the admin away mid-edit, so there it is a plain span
            // that looks the part.
            if (isPreview) {
              return (
                <span key={index} className={className}>
                  {button.label}
                  <Arrow />
                </span>
              );
            }

            const href = resolveAnnouncementUrl(button.url, fundId);
            // A link that needs a fund the reader does not have goes nowhere, so it is not drawn.
            if (!href) return null;

            const isExternal = /^https?:\/\//i.test(href);

            return (
              <a key={index} className={className} href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {button.label}
                <Arrow />
              </a>
            );
          })}
        </div>
      );

    case 'image':
      // A screenshot that shipped with the feature, cropped to the element it shows (HAT-1489).
      return block.src ? <AnnouncementImage className={styles.image} src={block.src} alt={block.text ?? ''} isPreview={isPreview} /> : null;

    case 'paragraph':
    default:
      return <p className={`${styles.paragraph} body-md-regular`}>{block.text}</p>;
  }
};

const Arrow = () => (
  <svg className={styles.linkArrow} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M5 12h13m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SystemMessageCard;
